import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';
interface Option {
  value: string;
  label: string;
}
interface SelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  label?: string;
  multi?: boolean;
  searchable?: boolean;
}
export function Select({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  label,
  multi = false,
  searchable = false
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const filteredOptions = options.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()));
  const handleSelect = (optionValue: string) => {
    if (multi) {
      if (value.includes(optionValue)) {
        onChange(value.filter(v => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    } else {
      onChange([optionValue]);
      setIsOpen(false);
    }
  };
  const removeValue = (e: React.MouseEvent, valToRemove: string) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== valToRemove));
  };
  const selectedLabels = value.map(v => options.find(o => o.value === v)?.label).filter(Boolean);
  return <div className="relative w-full" ref={containerRef} data-id="element-188">
      {label && <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-189">
          {label}
        </label>}

      <div className="min-h-[40px] w-full border border-gray-300 rounded-md bg-white px-3 py-2 flex items-center justify-between cursor-pointer hover:border-gray-400 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500" onClick={() => setIsOpen(!isOpen)} data-id="element-190">
        <div className="flex flex-wrap gap-1 flex-1" data-id="element-191">
          {value.length === 0 && <span className="text-gray-500" data-id="element-192">{placeholder}</span>}

          {multi ? value.map(val => {
          const opt = options.find(o => o.value === val);
          return <span key={val} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-193">
                  {opt?.label}
                  <X className="ml-1 h-3 w-3 cursor-pointer hover:text-blue-900" onClick={e => removeValue(e, val)} data-id="element-194" />
                </span>;
        }) : <span className="text-gray-900" data-id="element-195">{selectedLabels[0]}</span>}
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-196" />
      </div>

      {isOpen && <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm" data-id="element-197">
          {searchable && <div className="sticky top-0 z-10 bg-white px-2 py-2 border-b border-gray-100" data-id="element-198">
              <input type="text" className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} onClick={e => e.stopPropagation()} data-id="element-199" />
            </div>}

          {filteredOptions.length === 0 ? <div className="cursor-default select-none relative py-2 px-4 text-gray-700" data-id="element-200">
              No results found
            </div> : filteredOptions.map(option => <div key={option.value} className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 ${value.includes(option.value) ? 'bg-blue-50 text-blue-900' : 'text-gray-900'}`} onClick={() => handleSelect(option.value)} data-id="element-201">
                <span className={`block truncate ${value.includes(option.value) ? 'font-semibold' : 'font-normal'}`} data-id="element-202">
                  {option.label}
                </span>
                {value.includes(option.value) && <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600" data-id="element-203">
                    <Check className="h-4 w-4" data-id="element-204" />
                  </span>}
              </div>)}
        </div>}
    </div>;
}